<template>
    <div
        v-if="benefitUsageSummary"
        class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 sm:px-6 sm:py-5 mb-6"
    >
        <div
            class="grid grid-cols-1 sm:grid-cols-3 sm:gap-6 gap-4 max-sm:divide-y sm:divide-x divide-slate-200 dark:divide-slate-700"
        >
            <!-- Total Value Up To -->
            <div class="flex sm:flex-col max-sm:justify-between">
                <dt
                    class="text-base sm:text-sm font-normal sm:font-medium text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1"
                >
                    {{
                        benefitUsageSummary.is_count_based
                            ? 'Total available'
                            : 'Total value up to'
                    }}
                    <Tooltip
                        text="Total benefits available to use"
                        x-placement="center"
                        y-placement="top"
                        wrap
                    >
                        <template #trigger>
                            <Icon
                                icon="circle-info"
                                class="w-3.5 h-3.5 text-slate-400 cursor-help"
                            />
                        </template>
                    </Tooltip>
                </dt>
                <dd
                    class="text-base font-medium sm:text-xl sm:font-semibold text-slate-800 dark:text-slate-100"
                >
                    {{
                        formatValue(
                            benefitUsageSummary.total_annual_value,
                            benefitUsageSummary.is_count_based
                        )
                    }}{{ benefitUsageSummary.is_count_based ? '' : '/year' }}
                </dd>
            </div>

            <!-- Used -->
            <div
                class="flex sm:flex-col sm:pl-6 max-sm:pt-4 max-sm:justify-between"
            >
                <dt
                    class="text-base sm:text-sm font-normal sm:font-medium text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1"
                >
                    Used
                    <Tooltip
                        text="Benefits used in current period"
                        x-placement="center"
                        y-placement="top"
                        wrap
                    >
                        <template #trigger>
                            <Icon
                                icon="circle-info"
                                class="w-3.5 h-3.5 text-slate-400 cursor-help"
                            />
                        </template>
                    </Tooltip>
                </dt>
                <dd
                    class="text-base font-medium sm:text-xl sm:font-semibold text-slate-800 dark:text-slate-100"
                >
                    {{
                        formatValue(
                            benefitUsageSummary.used_ytd,
                            benefitUsageSummary.is_count_based
                        )
                    }}
                </dd>
            </div>

            <!-- Current utilization -->
            <div
                class="flex sm:flex-col sm:pl-6 max-sm:pt-4 max-sm:justify-between"
            >
                <dt
                    class="text-base sm:text-sm font-normal sm:font-medium text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1"
                >
                    Current utilization
                    <Tooltip
                        text="How much of total value you've used in current period"
                        x-placement="center"
                        y-placement="top"
                        wrap
                    >
                        <template #trigger>
                            <Icon
                                icon="circle-info"
                                class="w-3.5 h-3.5 text-slate-400 cursor-help"
                            />
                        </template>
                    </Tooltip>
                </dt>
                <div class="flex items-center relative">
                    <dd
                        class="text-base font-medium sm:text-xl sm:font-semibold text-slate-800 dark:text-slate-100"
                    >
                        {{ benefitUsageSummary.percent_used }}%
                    </dd>
                    <!-- Progress Arc - Desktop -->
                    <div class="w-16 h-10 absolute right-0 hidden sm:block">
                        <svg class="w-16 h-10" viewBox="0 0 48 24">
                            <defs>
                                <linearGradient
                                    id="desktop-gradient"
                                    x1="0%"
                                    y1="0%"
                                    x2="100%"
                                    y2="0%"
                                >
                                    <stop
                                        offset="0%"
                                        style="
                                            stop-color: #ef4444;
                                            stop-opacity: 1;
                                        "
                                    />
                                    <stop
                                        offset="25%"
                                        style="
                                            stop-color: #f97316;
                                            stop-opacity: 1;
                                        "
                                    />
                                    <stop
                                        offset="50%"
                                        style="
                                            stop-color: #eab308;
                                            stop-opacity: 1;
                                        "
                                    />
                                    <stop
                                        offset="75%"
                                        style="
                                            stop-color: #22c55e;
                                            stop-opacity: 1;
                                        "
                                    />
                                    <stop
                                        offset="100%"
                                        style="
                                            stop-color: #3b82f6;
                                            stop-opacity: 1;
                                        "
                                    />
                                </linearGradient>
                            </defs>
                            <!-- Background semicircle -->
                            <path
                                d="M 6 20 A 18 18 0 0 1 42 20"
                                fill="none"
                                stroke="#FED1C7"
                                stroke-width="6"
                            />
                            <!-- Progress semicircle -->
                            <path
                                d="M 6 20 A 18 18 0 0 1 42 20"
                                fill="none"
                                stroke="#FD421D"
                                stroke-width="6"
                                :stroke-dasharray="`${(benefitUsageSummary.percent_used / 100) * 56.5} 56.5`"
                                class="transition-all duration-300"
                            />
                        </svg>
                    </div>

                    <!-- Progress Arc - Mobile (28px height) -->
                    <div
                        class="w-11 h-7 absolute right-full block sm:hidden mr-2"
                    >
                        <svg class="w-11 h-7" viewBox="0 0 33 17">
                            <defs>
                                <linearGradient
                                    id="mobile-gradient"
                                    x1="0%"
                                    y1="0%"
                                    x2="100%"
                                    y2="0%"
                                >
                                    <stop
                                        offset="0%"
                                        style="
                                            stop-color: #ef4444;
                                            stop-opacity: 1;
                                        "
                                    />
                                    <stop
                                        offset="25%"
                                        style="
                                            stop-color: #f97316;
                                            stop-opacity: 1;
                                        "
                                    />
                                    <stop
                                        offset="50%"
                                        style="
                                            stop-color: #eab308;
                                            stop-opacity: 1;
                                        "
                                    />
                                    <stop
                                        offset="75%"
                                        style="
                                            stop-color: #22c55e;
                                            stop-opacity: 1;
                                        "
                                    />
                                    <stop
                                        offset="100%"
                                        style="
                                            stop-color: #3b82f6;
                                            stop-opacity: 1;
                                        "
                                    />
                                </linearGradient>
                            </defs>
                            <!-- Background semicircle -->
                            <path
                                d="M 4 14 A 12.5 12.5 0 0 1 29 14"
                                fill="none"
                                stroke="#FED1C7"
                                stroke-width="4"
                            />
                            <!-- Progress semicircle -->
                            <path
                                d="M 4 14 A 12.5 12.5 0 0 1 29 14"
                                fill="none"
                                stroke="#FD421D"
                                stroke-width="4"
                                :stroke-dasharray="`${(benefitUsageSummary.percent_used / 100) * 39.3} 39.3`"
                                class="transition-all duration-300"
                            />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useCardBenefitsStore } from '@/store/card-benefits';
import { storeToRefs } from 'pinia';
import Tooltip from '@/components/tooltip.vue';
import Icon from '@/components/ui/icon/icon.vue';

const cardBenefitsStore = useCardBenefitsStore();
const { filteredBenefitUsageSummary: benefitUsageSummary } =
    storeToRefs(cardBenefitsStore);

// Format money with commas for thousands. Cents render only when present
// (e.g. $12.95) so whole-dollar values stay clean ($845, not $845.00).
const formatMoney = (value) => {
    if (!value && value !== 0) return '$0';

    const hasCents = Number(value) % 1 !== 0;

    return '$' + Number(value).toLocaleString('en-US', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
    });
};

// Format value based on whether benefits are count-based or monetary
const formatValue = (value, isCountBased) => {
    if (isCountBased) {
        // For count-based benefits, just return the number
        return Math.round(Number(value) || 0).toString();
    }
    return formatMoney(value);
};
</script>
